import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, Card, CardContent, Button, Chip, Stack, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Alert,
  CircularProgress, Divider, IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";

const API_BASE = process.env.REACT_APP_API_DOCTOR_APPOINTMENT_URL;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } });
const TEAL = "#006D77";

const MyDoctorAppointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rescheduling, setRescheduling] = useState(null);
  const [dates, setDates] = useState([]);
  const [selDate, setSelDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selSlot, setSelSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const [uploadingId, setUploadingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm }

  const load = () => {
    setLoading(true);
    axios.get(`${API_BASE}/booking/my-doctor-appointments`, authHeaders())
      .then((r) => setAppointments(r.data || []))
      .catch(() => setError("Could not load your appointments."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const closeConfirm = () => setConfirmDialog(null);

  // ── Cancel appointment ──
  const requestCancel = (appt) => setConfirmDialog({
    title: "Cancel appointment?",
    message: `Cancel your appointment with Dr. ${appt.doctor?.name}? This frees the slot for others.`,
    onConfirm: () => doCancel(appt),
  });

  const doCancel = async (appt) => {
    closeConfirm();
    try {
      await axios.put(`${API_BASE}/booking/doctor-appointments/${appt.id}/cancel`, {}, authHeaders());
      load();
    } catch { setError("Could not cancel. Please try again."); }
  };

  // ── Reschedule ──
  const openReschedule = async (appt) => {
    setRescheduling(appt);
    setSelDate(null); setSelSlot(null); setSlots([]);
    try {
      const res = await axios.get(`${API_BASE}/booking/doctors/${appt.doctor.id}/available-dates`, authHeaders());
      setDates(res.data || []);
    } catch { setDates([]); }
  };

  const pickDate = async (date) => {
    setSelDate(date); setSelSlot(null);
    try {
      const res = await axios.get(`${API_BASE}/booking/doctors/${rescheduling.doctor.id}/slots`, {
        ...authHeaders(), params: { date },
      });
      setSlots(res.data || []);
    } catch { setSlots([]); }
  };

  const confirmReschedule = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/booking/doctor-appointments/${rescheduling.id}/reschedule`,
        { slot_id: selSlot.id }, authHeaders());
      setRescheduling(null);
      load();
    } catch (e) {
      alert(e.response?.status === 409
        ? "That slot was just taken. Pick another."
        : "Could not reschedule. Please try again.");
    } finally { setBusy(false); }
  };

  // ── Upload prescription (with confirm) ──
  const requestUpload = (appt, files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setConfirmDialog({
      title: "Upload prescription?",
      message: `You're about to upload ${arr.length} file(s) for Dr. ${appt.doctor?.name}. Continue?`,
      onConfirm: () => doUpload(appt, arr),
    });
  };

  const doUpload = async (appt, files) => {
    closeConfirm();
    setUploadingId(appt.id);
    setError("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("prescription", f));
      await axios.post(
        `${API_BASE}/booking/doctor-appointments/${appt.appointment_code}/prescription`,
        fd,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}`, "Content-Type": "multipart/form-data" } }
      );
      load();
    } catch {
      setError("Could not upload prescription. Please try again.");
    } finally {
      setUploadingId(null);
    }
  };

  // ── Delete prescription (with confirm) ──
  const requestDelete = (appt, url) => setConfirmDialog({
    title: "Remove prescription?",
    message: "This prescription file will be permanently removed. Continue?",
    onConfirm: () => doDelete(appt, url),
  });

  const doDelete = async (appt, url) => {
    closeConfirm();
    try {
      await axios.delete(`${API_BASE}/booking/doctor-appointments/${appt.appointment_code}/prescription`, {
        ...authHeaders(),
        data: { url },
      });
      load();
    } catch {
      setError("Could not remove prescription. Please try again.");
    }
  };

  if (loading) return <Box textAlign="center" py={8}><CircularProgress sx={{ color: TEAL }} /></Box>;

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
        sx={{ mb: 2, textTransform: "none", color: TEAL, fontWeight: 600 }}>
        Back
      </Button>
      <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom>My Appointments</Typography>
      <Typography color="text.secondary" mb={3}>Reschedule, cancel, or upload a prescription for your consultations.</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2}>
        {appointments.map((a) => {
          const cancelled = a.status === "cancelled";
          const uploading = uploadingId === a.id;
          return (
            <Card key={a.id} variant="outlined" sx={{ borderRadius: 3, opacity: cancelled ? 0.6 : 1 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={a.doctor?.photo_url} sx={{ bgcolor: TEAL, width: 52, height: 52 }}>
                      {a.doctor?.name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>Dr. {a.doctor?.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{a.doctor?.specialization}</Typography>
                    </Box>
                  </Stack>
                  <Chip label={cancelled ? "Cancelled" : "Confirmed"}
                    color={cancelled ? "default" : "success"} size="small" />
                </Stack>

                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2"><b>Code:</b> {a.appointment_code}</Typography>
                <Typography variant="body2"><b>Patient:</b> {a.patient_name}</Typography>
                <Typography variant="body2">
                  <b>When:</b> {a.slot?.date} · {a.slot?.start_time} - {a.slot?.end_time}
                </Typography>
                <Typography variant="body2"><b>Fee:</b> ₹{a.fee}</Typography>

                {/* Prescriptions */}
                <Box mt={1.5}>
                  {(a.prescription_urls || []).length > 0 && (
                    <Stack spacing={0.5} mb={1}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        Prescriptions ({a.prescription_urls.length})
                      </Typography>
                      {a.prescription_urls.map((url, i) => (
                        <Stack key={url} direction="row" spacing={1} alignItems="center">
                          <DescriptionIcon fontSize="small" sx={{ color: TEAL }} />
                          <Button size="small" href={url} target="_blank" rel="noopener"
                            sx={{ textTransform: "none", color: TEAL, minWidth: 0 }}>
                            Prescription {i + 1}
                          </Button>
                          {!cancelled && (
                            <IconButton size="small" color="error" onClick={() => requestDelete(a, url)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {!cancelled && (
                    <Button component="label" size="small" startIcon={<UploadFileIcon />}
                      disabled={uploading} variant="outlined"
                      sx={{ textTransform: "none", color: TEAL, borderColor: TEAL }}>
                      {uploading
                        ? "Uploading..."
                        : (a.prescription_urls || []).length > 0 ? "Add more" : "Upload Prescription"}
                      <input hidden type="file" accept="image/*,application/pdf" multiple
                        onChange={(e) => { requestUpload(a, e.target.files); e.target.value = ""; }} />
                    </Button>
                  )}
                </Box>

                {!cancelled && (
                  <Stack direction="row" spacing={1.5} mt={2}>
                    <Button variant="outlined" sx={{ textTransform: "none", color: TEAL, borderColor: TEAL }}
                      onClick={() => openReschedule(a)}>Reschedule</Button>
                    <Button variant="outlined" color="error" sx={{ textTransform: "none" }}
                      onClick={() => requestCancel(a)}>Cancel</Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          );
        })}
        {appointments.length === 0 && <Typography color="text.secondary">You have no appointments yet.</Typography>}
      </Stack>

      {/* Reschedule dialog */}
      <Dialog open={!!rescheduling} onClose={() => setRescheduling(null)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Reschedule with Dr. {rescheduling?.doctor?.name}</DialogTitle>
        <DialogContent>
          <Typography fontWeight={600} mt={1} mb={1}>Pick a date</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
            {dates.map((d) => (
              <Chip key={d} clickable color={selDate === d ? "primary" : "default"}
                onClick={() => pickDate(d)}
                label={new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} />
            ))}
            {dates.length === 0 && <Typography color="text.secondary">No open dates.</Typography>}
          </Stack>

          {selDate && (
            <>
              <Typography fontWeight={600} mb={1}>Pick a slot</Typography>
              <Grid container spacing={1}>
                {slots.map((s) => (
                  <Grid item xs={6} sm={4} key={s.id}>
                    <Button fullWidth variant={selSlot?.id === s.id ? "contained" : "outlined"}
                      sx={{ textTransform: "none", ...(selSlot?.id === s.id && { bgcolor: TEAL }) }}
                      onClick={() => setSelSlot(s)}>
                      {s.start_time} - {s.end_time}
                    </Button>
                  </Grid>
                ))}
                {slots.length === 0 && <Grid item xs={12}><Typography color="text.secondary">No open slots.</Typography></Grid>}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRescheduling(null)} sx={{ textTransform: "none" }}>Close</Button>
          <Button variant="contained" disabled={!selSlot || busy} onClick={confirmReschedule}
            sx={{ bgcolor: TEAL, textTransform: "none" }}>
            {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Confirm Reschedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shared confirm dialog (upload / delete / cancel) */}
      <Dialog open={!!confirmDialog} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>{confirmDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">{confirmDialog?.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={() => confirmDialog?.onConfirm?.()}
            sx={{ bgcolor: TEAL, textTransform: "none" }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyDoctorAppointments;